import React from 'react';
import { Chip } from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import WorkIcon from '@mui/icons-material/Work';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ExploreIcon from '@mui/icons-material/Explore';
import MapIcon from '@mui/icons-material/Map';
import { DESIGN_TOKENS } from '../../styles/theme';
import type { ListingStatus, Permission } from '../../types';
import { useI18n } from '../../i18n';

// ============================================================
// STATUS CHIP — LISTING STATUS
// ============================================================

interface ListingStatusChipProps {
  status?: ListingStatus | string | null;
}

function normalizeListingStatus(
  status?: ListingStatus | string | null
): Exclude<ListingStatus, 'PUBLISHED_OPEN' | 'IN_REALIZATION'> {
  if (status === 'PUBLISHED_OPEN') return 'OPEN';
  if (status === 'IN_REALIZATION') return 'CLOSED';
  if (status === 'CLOSED' || status === 'CANCELED' || status === 'OPEN') return status;
  return 'OPEN';
}

export function ListingStatusChip({ status }: ListingStatusChipProps) {
  const { t } = useI18n();
  const listingStatusConfig: Record<
    Exclude<ListingStatus, 'PUBLISHED_OPEN' | 'IN_REALIZATION'>,
    { label: string; color: 'success' | 'info' | 'default' | 'error' }
  > = {
    OPEN: { label: t('status.open'), color: 'success' },
    CLOSED: { label: t('status.closed'), color: 'default' },
    CANCELED: { label: t('status.canceled'), color: 'error' },
  };

  const config = listingStatusConfig[normalizeListingStatus(status)];
  return (
    <Chip
      label={config.label}
      color={config.color}
      size="small"
      sx={{ fontWeight: 600, fontSize: '0.7rem' }}
    />
  );
}

// ============================================================
// PILOT PREFERENCE ICONS
// ============================================================

export function usePilotPreferenceIcons() {
  const { t } = useI18n();

  return React.useMemo(
    () => [
      { id: 'location', component: <LocationOnIcon />, label: t('preferences.location') },
      { id: 'home', component: <HomeIcon />, label: t('preferences.home') },
      { id: 'work', component: <WorkIcon />, label: t('preferences.work') },
      { id: 'car', component: <DirectionsCarIcon />, label: t('preferences.car') },
      { id: 'explore', component: <ExploreIcon />, label: t('preferences.explore') },
      { id: 'map', component: <MapIcon />, label: t('preferences.map') },
    ],
    [t]
  );
}

export function getPreferenceIcon(id?: string, size: 'small' | 'medium' = 'medium', color: string = DESIGN_TOKENS.colors.primary) {
  const iconMap = {
    location: <LocationOnIcon />,
    home: <HomeIcon />,
    work: <WorkIcon />,
    car: <DirectionsCarIcon />,
    explore: <ExploreIcon />,
    map: <MapIcon />,
  } as const;
  const component = iconMap[id as keyof typeof iconMap] ?? <LocationOnIcon />;

  return React.cloneElement(component as React.ReactElement, {
    fontSize: size,
    sx: { color }
  });
}

// ============================================================
// PERMISSION CHIP
// ============================================================

const PERMISSION_COLORS: Record<Permission, string> = {
  BF2: DESIGN_TOKENS.colors.slate,
  BF3: DESIGN_TOKENS.colors.primary,
  BF4: DESIGN_TOKENS.colors.primaryDark,
  HIPO: DESIGN_TOKENS.colors.accent,
};

interface PermissionChipProps {
  permission: Permission;
}

export function PermissionChip({ permission }: PermissionChipProps) {
  const backgroundColor = PERMISSION_COLORS[permission] ?? DESIGN_TOKENS.colors.slate;

  return (
    <Chip
      label={permission}
      size="small"
      sx={{
        backgroundColor,
        color: '#FFFFFF',
        fontWeight: 700,
        fontSize: '0.7rem',
        height: 22,
      }}
    />
  );
}
